import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

export interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant' | 'system' | 'error';
  timestamp: string;
  metadata?: Record<string, any>;
}

export interface ConversationState {
  id: string;
  messages: Message[];
  participants: {
    id: string;
    name: string;
    type: 'user' | 'persona' | 'agent' | 'team';
    metadata?: Record<string, any>;
  }[];
  status: 'idle' | 'loading' | 'error';
  error?: string;
  metadata?: Record<string, any>;
}

type ConversationAction =
  | { type: 'ADD_MESSAGE'; payload: Omit<Message, 'id' | 'timestamp'> }
  | { type: 'SET_MESSAGES'; payload: Message[] }
  | { type: 'SET_STATUS'; payload: ConversationState['status'] }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'SET_METADATA'; payload: Record<string, any> }
  | { type: 'RESET' };

interface ConversationContextType {
  state: ConversationState;
  sendMessage: (content: string, metadata?: Record<string, any>) => Promise<void>;
  resetConversation: () => void;
  setMessages: (messages: Message[]) => void;
}

const initialState: ConversationState = {
  id: '',
  messages: [],
  participants: [],
  status: 'idle',
  metadata: {}
};

const ConversationContext = createContext<ConversationContextType | undefined>(undefined);

function conversationReducer(state: ConversationState, action: ConversationAction): ConversationState {
  switch (action.type) {
    case 'ADD_MESSAGE':
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            id: uuidv4(),
            timestamp: new Date().toISOString(),
            ...action.payload
          }
        ]
      };
    case 'SET_MESSAGES':
      return {
        ...state,
        messages: action.payload
      };
    case 'SET_STATUS':
      return {
        ...state,
        status: action.payload,
        error: action.payload === 'error' ? state.error : undefined
      };
    case 'SET_ERROR':
      return {
        ...state,
        status: 'error',
        error: action.payload
      };
    case 'SET_METADATA':
      return {
        ...state,
        metadata: {
          ...state.metadata,
          ...action.payload
        }
      };
    case 'RESET':
      return {
        ...initialState,
        id: state.id
      };
    default:
      return state;
  }
}

export interface ConversationProviderProps {
  children: React.ReactNode;
  sessionId: string;
  adapter: ConversationAdapter;
  initialMessages?: Message[];
  participants?: ConversationState['participants'];
  metadata?: Record<string, any>;
}

// Adapter interface for different conversation backends
export interface ConversationAdapter {
  sendMessage: (sessionId: string, content: string, metadata?: Record<string, any>) => Promise<Message>;
  getMessages: (sessionId: string) => Promise<Message[]>;
  initialize?: (sessionId: string, metadata?: Record<string, any>) => Promise<void>;
}

export const ConversationProvider: React.FC<ConversationProviderProps> = ({
  children,
  sessionId,
  adapter,
  initialMessages = [],
  participants = [],
  metadata = {}
}) => {
  const [state, dispatch] = useReducer(conversationReducer, {
    ...initialState,
    id: sessionId,
    messages: initialMessages,
    participants,
    metadata
  });

  // Synchronous within-tick guard against double-sends. `state.status`
  // alone is not sufficient: two rapid-fire sendMessage calls in the
  // same microtask share the captured closure and both see status
  // 'idle' until React re-renders. The ref flips synchronously, so
  // the second call in the same tick sees the true in-flight value.
  const inFlightRef = useRef(false);

  useEffect(() => {
    const initializeConversation = async () => {
      if (adapter.initialize) {
        try {
          await adapter.initialize(sessionId, metadata);
        } catch (error) {
          console.error('Failed to initialize conversation:', error);
          dispatch({ type: 'SET_ERROR', payload: 'Failed to initialize conversation' });
        }
      }

      if (initialMessages.length === 0) {
        try {
          const messages = await adapter.getMessages(sessionId);
          dispatch({ type: 'SET_MESSAGES', payload: messages });
        } catch (error) {
          console.error('Failed to load messages:', error);
          dispatch({ type: 'SET_ERROR', payload: 'Failed to load messages' });
        }
      }
    };

    initializeConversation();
  }, [sessionId, adapter, initialMessages.length]);

  const sendMessage = async (content: string, metadata?: Record<string, any>) => {
    if (!content.trim()) return;

    // Context-level double-send guard — blocks programmatic callers
    // (retry buttons, SendTo, keyboard shortcuts) AND form handlers.
    // UI-level form gates are NOT sufficient on their own; a second
    // sendMessage dispatched while loading would create duplicate
    // user messages + racing adapter calls.
    if (inFlightRef.current || state.status === 'loading') {
      console.warn('[ConversationContext] sendMessage called while loading — ignored');
      return;
    }
    inFlightRef.current = true;

    // Add user message immediately
    dispatch({
      type: 'ADD_MESSAGE',
      payload: {
        content,
        role: 'user',
        metadata
      }
    });

    dispatch({ type: 'SET_STATUS', payload: 'loading' });

    try {
      // Send to backend and get response
      const response = await adapter.sendMessage(sessionId, content, metadata);

      // Add assistant response
      dispatch({
        type: 'ADD_MESSAGE',
        payload: response
      });

      dispatch({ type: 'SET_STATUS', payload: 'idle' });
    } catch (error) {
      console.error('Failed to send message:', error);

      // Add error message
      dispatch({
        type: 'ADD_MESSAGE',
        payload: {
          content: 'Sorry, there was an error processing your message. Please try again.',
          role: 'error',
          metadata: { error }
        }
      });

      dispatch({ type: 'SET_ERROR', payload: 'Failed to send message' });
    } finally {
      inFlightRef.current = false;
    }
  };

  const resetConversation = () => {
    dispatch({ type: 'RESET' });
  };

  const setMessages = (messages: Message[]) => {
    dispatch({ type: 'SET_MESSAGES', payload: messages });
  };

  return (
    <ConversationContext.Provider
      value={{
        state,
        sendMessage,
        resetConversation,
        setMessages
      }}
    >
      {children}
    </ConversationContext.Provider>
  );
};

export const useConversation = () => {
  const context = useContext(ConversationContext);
  if (context === undefined) {
    throw new Error('useConversation must be used within a ConversationProvider');
  }
  return context;
};
